import { useCallback, useEffect } from 'react'
import { fetchNewCropTasks } from '../components/Prescription/NewCrop/api'
import appActions from '../features/app/actions'
import { selectIsOTTMode, selectStoreId } from '../features/app/selectors'
import { selectUsername } from '../features/auth/selectors'
import azureCommunicationActions from '../features/azureCommunication/actions'
import {
	connectToSocketKeepAlive,
	disconnectFromSocketKeepAlive,
} from '../features/socket/keepAlive/keepAliveSocket'
import {
	connectToSocketUserOnlineStatus,
	disconnectFromSocketUserOnlineStatus,
} from '../features/socket/userOnlineStatus/userOnlineStatusSocket'
import { selectStoreByStoreId } from '../features/stores/selectors'
import {
	convertDateForQueryString,
	FIFTEEN_SECONDS,
	FIVE_MINUTES,
	isValidDate,
} from '../libs/time'
import { ExamMode, ExamStatus } from '../model/exam'
import { InstrumentType } from '../model/instrumentsApi'
import { Id } from '../model/model'
import { Platform } from '../model/users'
import { useTeloNavigate } from '../routing/teloNavigate'
import { useTeloDispatch, useTeloSelector } from '../store'
import {
	getFetchAssessments,
	getFetchExam,
	getFetchExams,
	getFetchInstrumentData,
	getFetchInstrumentsInRoom,
	getFetchReports,
	getFetchStages,
	getFetchUser,
} from './throtteledFetchActions'

export const usePollingExams = ({
	statusFrom,
	statusTo,
	states,
	stores,
	dateFrom,
	languages,
	mode,
	doctor,
	stripped,
	storeId,
}: {
	statusFrom?: ExamStatus
	statusTo?: ExamStatus
	states?: string
	stores?: string
	dateFrom?: Date
	languages?: string
	mode?: ExamMode
	doctor?: string
	stripped?: boolean
	storeId?: Id
}) => {
	const dispatch = useTeloDispatch()
	const isOttMode = useTeloSelector(selectIsOTTMode)

	useEffect(() => {
		// if states or languages are passed they must be populated
		if (
			(states !== undefined && !states.length) ||
			(languages !== undefined && !languages.length) ||
			(dateFrom && !isValidDate(dateFrom)) ||
			(doctor && stores !== undefined && stores.length === 0)
		) {
			return
		}

		// if i'm in ott mode i have to do nothing
		if (isOttMode) {
			return
		}

		const fetchExams = getFetchExams(
			{
				statusFrom,
				statusTo,
				states,
				stores,
				dateFrom: dateFrom ? convertDateForQueryString(dateFrom) : undefined,
				languages,
				mode,
				doctor,
				stripped,
				storeId,
			},
			dispatch,
		)

		fetchExams()

		let fetchExamsInterval: number | undefined
		fetchExamsInterval = window.setInterval(fetchExams, FIVE_MINUTES)

		return function cleanUp() {
			fetchExamsInterval && clearInterval(fetchExamsInterval)
		}
	}, [
		dispatch,
		statusFrom,
		statusTo,
		languages,
		states,
		stores,
		mode,
		dateFrom,
		doctor,
		storeId,
		stripped,
		isOttMode,
	])
}

export const usePollingExamById = (examId?: Id) => {
	const dispatch = useTeloDispatch()

	useEffect(() => {
		if (!examId) {
			return
		}

		const fetchExams = getFetchExam(examId, true, dispatch)

		fetchExams()
	}, [dispatch, examId])
}

export const usePollingUser = (username: string) => {
	const dispatch = useTeloDispatch()
	const navigate = useTeloNavigate()
	useEffect(() => {
		const fetchUser = getFetchUser(username, dispatch, navigate)
		fetchUser()

		let fetchInterval: number | undefined
		fetchInterval = window.setInterval(fetchUser, FIVE_MINUTES)

		return function cleanUp() {
			fetchInterval && clearInterval(fetchInterval)
		}
	}, [dispatch, navigate, username])
}

export const usePollingAssessments = (isAppInsideEclips?: boolean) => {
	const dispatch = useTeloDispatch()
	useEffect(() => {
		if (isAppInsideEclips) {
			return
		}
		const fetchAssessments = getFetchAssessments(dispatch)

		fetchAssessments()

		let fetchInterval: number | undefined
		fetchInterval = window.setInterval(fetchAssessments, FIVE_MINUTES)

		return function cleanUp() {
			fetchInterval && clearInterval(fetchInterval)
		}
	}, [dispatch, isAppInsideEclips])
}

export const useGetInstrumentData = (
	examId: Id,
	examLoaded: boolean,
	autoUpdate: boolean,
	instrumentType: InstrumentType,
) => {
	const dispatch = useTeloDispatch()
	const isOttMode = useTeloSelector(selectIsOTTMode)
	useEffect(() => {
		if (isOttMode) {
			return
		}

		const fetchInstrumentData = getFetchInstrumentData(
			examId,
			instrumentType,
			dispatch,
		)

		if (examLoaded && examId) {
			fetchInstrumentData()
		}

		let intervalId: number | undefined

		if (autoUpdate && examLoaded && examId && intervalId === undefined) {
			intervalId = window.setInterval(fetchInstrumentData, FIVE_MINUTES)
		}
		return function cleanUp() {
			intervalId && clearInterval(intervalId)
		}
	}, [dispatch, examId, examLoaded, autoUpdate, instrumentType, isOttMode])
}

export const usePollingStages = () => {
	const dispatch = useTeloDispatch()
	const storeId = useTeloSelector(selectStoreId)
	const store = useTeloSelector(selectStoreByStoreId(storeId))
	const storeLoaded = !!store
	useEffect(() => {
		let intervalId: undefined | number

		if (storeId && storeLoaded) {
			const fetchStages = getFetchStages(storeId, dispatch)
			fetchStages()
			intervalId = window.setInterval(fetchStages, FIVE_MINUTES)
		}

		return function cleanUp() {
			intervalId && clearInterval(intervalId)
		}
	}, [dispatch, storeId, storeLoaded])
}

export const usePollingInstrumentsInRoom = (
	storeId: string,
	stageId: Id,
	roomId: Id,
) => {
	const dispatch = useTeloDispatch()

	useEffect(() => {
		const updateInstrumentsInRoom = getFetchInstrumentsInRoom(
			storeId,
			stageId,
			roomId,
			dispatch,
		)

		updateInstrumentsInRoom()

		const intervalId = setInterval(updateInstrumentsInRoom, FIVE_MINUTES)

		return function cleanUp() {
			intervalId && clearInterval(intervalId)
		}
	}, [dispatch, storeId, stageId, roomId])
}

export const useKeepAliveUser = () => {
	const username = useTeloSelector(selectUsername)

	useEffect(() => {
		connectToSocketKeepAlive({ username })
		return () => {
			disconnectFromSocketKeepAlive()
		}
	}, [username])
}

export const useCheckUserOnline = (platform: Platform, username: string) => {
	const dispatch = useTeloDispatch()

	const onUserOnlineStatusChange = useCallback(
		(online: boolean) => {
			dispatch(
				azureCommunicationActions.setCalleeOnlineStatus({ username, online }),
			)
		},
		[dispatch, username],
	)

	useEffect(() => {
		if (!username) {
			return
		}
		connectToSocketUserOnlineStatus({
			username,
			platform: platform === 'teleoptometry' ? 'connect' : 'cockpit',
			onUserOnlineStatusChange,
		})

		return () => {
			disconnectFromSocketUserOnlineStatus()
		}
	}, [dispatch, username, platform, onUserOnlineStatusChange])
}

export const usePollingReports = () => {
	const dispatch = useTeloDispatch()
	useEffect(() => {
		const fetchReports = getFetchReports(dispatch)

		fetchReports()

		let fetchInterval: number | undefined
		fetchInterval = window.setInterval(fetchReports, FIFTEEN_SECONDS)

		return function cleanUp() {
			fetchInterval && clearInterval(fetchInterval)
		}
	}, [dispatch])
}

export const useGetNewCropStatusCount = (storeId: string) => {
	const dispatch = useTeloDispatch()

	useEffect(() => {
		if (storeId === undefined || storeId.length === 0) {
			return
		}

		const getNewCropStatusCount = () =>
			fetchNewCropTasks(storeId).then(count => {
				dispatch(appActions.setNewCropStatusCount(count))
			})

		getNewCropStatusCount()

		let fetchInterval: number | undefined
		fetchInterval = window.setInterval(getNewCropStatusCount, FIVE_MINUTES)

		return function cleanUp() {
			fetchInterval && clearInterval(fetchInterval)
		}
	}, [dispatch, storeId])
}
